import { useMemo } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { MdRotateLeft } from "react-icons/md";
import moment from "moment";
import BreadcrumbDates from "../../../../components/core/blocks/BreadcrumbDates";
import SummaryCard from "../../../../components/core/blocks/SummaryCard";
import Upload from "../../../../components/core/blocks/Upload";
import Loading from "../../../../components/core/blocks/Loading";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import { Button } from "../../../../components/core/basic/Button";
import { RT8ColumnSelector } from "../../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../../components/fields/base/TextField";
import { BulkActionButton } from "../../../../components/table/BulkActionButton";
import { RT8Pagination } from "../../../../components/table/Pagination";
import { Table } from "../../../../components/table/Table";
import { commaSearchFilterFn } from "../../../../components/table/filterFns";
import { createAppColumnHelper, useClientTable } from "../../../../components/table/tableFeatures";
import { usePersistedTableState } from "../../../../components/table/usePersistedTableState";
import { resetTable, selectionStateToSelectedIds } from "../../../../components/table/utils";
import { DatesProvider } from "../../../../dates/DatesContext";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";
import { useAppSelector } from "../../../../redux/hooks";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import {
  useDeleteSQPReports,
  useInvalidateSQPReports,
  useSQPReports,
} from "../../../../modules/sqpr/api/useSQPReports";
import { type SQPReport } from "../../../../zodios/apiDefinitions/api/sqpReports";
import { HStack } from "../../../../../styled-system/jsx";
import { css } from "../../../../../styled-system/css";

export const Route = createFileRoute("/_authenticated/user/searchqueryperformance/")({
  component: SearchQueryPerformanceReports,
});

const columnHelper = createAppColumnHelper<SQPReport>();

const reportSearchFilterFn = commaSearchFilterFn<SQPReport>(
  (row) => `${row.upload?.name ?? ""} ${row.reporting_range}`,
);

const ReportsTable = ({ reports }: { reports: SQPReport[] | undefined }) => {
  const { t } = useTranslation(["sqpr", "common"]);
  const navigate = useNavigate();
  const hasDebugModule = useModuleEnabled("DEBUG");
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const dateFormatter = useDateFormatter("medium");

  const deleteReports = useDeleteSQPReports();
  const invalidateReports = useInvalidateSQPReports();

  const columns = useMemo(() => {
    // Date-only strings: parse as local dates so they don't shift a day west of UTC.
    const formatDate = (value: string) => (value ? dateFormatter.format(moment(value).toDate()) : "-");
    return [
      // A report can outlive its upload record, so the name may be missing.
      columnHelper.accessor((row) => row.upload?.name ?? undefined, {
        id: "name",
        header: t(($) => $.sqpr["Name"]),
        size: 350,
        enableHiding: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        cell: ({ row, getValue }) => (
          <Link to="/user/searchqueryperformance/$id" params={{ id: String(row.original.id) }}>
            {getValue() ?? "-"}
          </Link>
        ),
      }),
      columnHelper.accessor("brand_or_asin", {
        header: t(($) => $.sqpr["Report Type"]),
        size: 200,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor("reporting_range", {
        header: t(($) => $.sqpr["Report Range"]),
        size: 130,
        enableResizing: false,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor("start_date", {
        header: t(($) => $.sqpr["Start Date"]),
        size: 140,
        enableResizing: false,
        cell: ({ getValue }) => formatDate(getValue()),
      }),
      columnHelper.accessor("end_date", {
        header: t(($) => $.sqpr["End Date"]),
        size: 140,
        enableResizing: false,
        cell: ({ getValue }) => formatDate(getValue()),
      }),
    ];
  }, [t, dateFormatter]);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "sqprReportsTable",
    initialState: { sorting: [{ id: "end_date", desc: true }] },
  });

  const table = useClientTable({
    data: reports,
    columns,
    getRowId: (row) => String(row.id),
    enableRowSelection: true,
    globalFilterFn: reportSearchFilterFn,
    initialState,
    state,
    ...setters,
  });

  const selected = selectionStateToSelectedIds(table.state.rowSelection).map(Number);

  return (
    <SummaryCard
      title={t(($) => $.sqpr["Search Query Performance Reports"])}
      status={
        hasDebugModule ?
          <Button
            variant={"link"}
            className={css({ paddingStart: "large", fontSize: "xl" })}
            onPress={() => {
              void navigate({ to: "/user/report/sqpr_report" });
            }}
          >
            {t(($) => $.sqpr["View Generated Reports"])}
          </Button>
        : null
      }
    >
      <HStack justifyContent="end">
        <BulkActionButton
          selected={selected}
          options={[
            {
              label: t(($) => $.sqpr["Delete Report"]),
              action: () => {
                deleteReports.mutate(selected, { onSuccess: () => table.resetRowSelection() });
              },
              confirmationMessage: (
                <p>
                  {t(($) => $.sqpr["Are you sure you want to delete the selected reports?"], {
                    count: selected.length,
                  })}
                </p>
              ),
            },
          ]}
        />
      </HStack>
      <HStack padding={"small"} fontSize={"large"}>
        <Button
          variant={"contentOnly"}
          aria-label={t(($) => $.common.table["Reset table filters"])}
          onPress={() => resetTable(table)}
          fontSize={"xxlarge"}
        >
          <MdRotateLeft />
        </Button>
        <RT8ColumnSelector table={table} />
        <SearchInput
          aria-label={t(($) => $.common.actions["Search"])}
          isExpandable
          css={{ flex: "0 1 350px" }}
          value={table.state.globalFilter ?? ""}
          onChange={table.setGlobalFilter}
        />
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
      <Upload
        uploadType="sqp_report"
        fileType="text/csv"
        details={{
          marketplace: marketPlace,
          autoProcess: true,
        }}
        onSuccess={() => {
          void invalidateReports();
        }}
      />
    </SummaryCard>
  );
};

function SearchQueryPerformanceReports() {
  const { t } = useTranslation(["sqpr"]);
  const { data: reports, isLoading } = useSQPReports();

  return (
    <DatesProvider>
      <BreadcrumbDates
        items={[{ name: t(($) => $.sqpr["Search Query Performance"]), href: "/user/searchqueryperformance" }]}
        hideDateSelector
      />
      <PageContainer withBreadcrumb>
        <div className="fs-standard">
          {isLoading ?
            <Loading />
          : <ReportsTable reports={reports} />}
        </div>
      </PageContainer>
    </DatesProvider>
  );
}
